import { gql } from '@apollo/client';
import { GAME_STATUS } from '@crazygames/game-types/src/game/game-status.type';
import { GameCoverWithUploadedFile } from '../../../../../common/graphql/games/game';
import { Developer } from '@crazygames/game-types/src/developer/developer.type';
import { TRANSLATED_ENTRY_FRAGMENT } from '../../../../../common/graphql/translatedEntry/translated-entry-graphql';
import { DeveloperDailyRevenueStat, DeveloperGameStatsForTimespan } from '../Games/userDeveloperSubmission-graphql';
import { TranslatedEntry } from '../../../../../common/i18n';
import { MultiplayerOptions } from './GameBaseForm/baseFormHelper';
import { GAME_BUILD_FRAGMENT, GameBuild } from '../../Admin/Game/gql/game-build-graphql';
import { RevenueShareVersion } from '../../Admin/Game/Commercial/Commercial/revenue-share-graphql';
import { SubmissionStatusChange } from '../../Admin/Game/gql/submission-graphql';
import { GameJamId } from '@crazygames/gamejam/game-jam-config';
import { LegalFormStatus } from '@crazygames/game-types/src/submission/legal-form-status.type';
import { SUBMISSION_STATUS } from '@crazygames/game-types/src/submission/submission-status.type';
import { SubmissionVideo } from '@crazygames/graph-types/submission/submission-video.type';
import { BasicLaunchStatsAndBenchmarks } from '@crazygames/stats-types/stats.types';
import { DailyDeveloperGameAnalytics } from '@crazygames/graph-types/game/developer-daily-analytics.type';
import { BENCHMARK_STATS_FRAGMENT } from '@crazygames/graph-types/game/developer-analytics-benchmarks.type';
import { GAME_ADVERTISEMENT_STATS_FRAGMENT } from '@crazygames/graph-types/game/game-ad-stats.type';

/**
 * Submission interface for developer logic
 */
export interface Submission {
  gameName: string;
  gameStatus: GAME_STATUS;
  id: string;
  status: SUBMISSION_STATUS;
  createdAt: string;
  submittedOn: string;
  feedback: FeedbackType;
  description: string;
  controls: string;
  playStoreLink: string;
  appStoreLink: string;
  steamStoreLink: string;
  categoryId: string;
  categoryName: string;
  tagIds: string[];
  gameCovers: GameCoverWithUploadedFile[] | null;
  slug: string;
  mobileFriendly: boolean;
  filesChanged?: boolean;
  coverChanged?: boolean;
  developer?: Pick<Developer, 'id' | 'hasReleaseRights'>; // based on the attributes queried in SUBMISSION_FRAGMENT
  playStoreDownloads?: number;
  appStoreDownloads?: number;
  steamDownloads?: number;
  multiplayerOptions?: MultiplayerOptions;
  gameJamId: GameJamId | null;
  gameBuilds: GameBuild[];
  rawRevenueShareOptions?: string;
  revenueShareVersion?: RevenueShareVersion;
  isInvited?: boolean;
  videos: SubmissionVideo | null;
  isFullLaunch: boolean;
  basicLaunchOn: string | null;
  basicLaunchExpiresOn: string | null;
  legalFormStatus: LegalFormStatus;
  statusChanges: SubmissionStatusChange[];
  game?: DeveloperGame;
  acceptedTermsVersion?: string;
  skipFileChecks?: boolean;
}

export type FeedbackType = Review[];

export interface Review {
  comment: string;
  postedOn: string;
  authorName: string;
}

export interface GetMySubmissionQuery {
  me: {
    submission: Submission;
  };
}

/**
 * Most of game fields on graph require GAME_R permissions.
 * This is a minimal game interface, containing public readable fields, used to display some info to the developer.
 */
export interface DeveloperGame {
  id: string;
  addedOn: string;
  rawLeaderboardOptions?: string | null;
}

export const SUBMISSION_FRAGMENT = gql`
  fragment SubmissionFragment on Submission {
    id
    slug
    gameName
    gameStatus
    categoryId
    categoryName
    tagIds
    description
    controls
    steamStoreLink
    appStoreLink
    playStoreLink
    playStoreDownloads
    appStoreDownloads
    steamDownloads
    status
    createdAt
    submittedOn
    slug
    mobileFriendly
    gameCovers {
      size
      uploadedFile {
        uploadId
        path
        size
        temporaryUrl
      }
    }
    feedback {
      comment
      authorName
      postedOn
    }
    filesChanged
    coverChanged
    iframeLinkChanged
    developer {
      id
      hasReleaseRights
    }
    multiplayerOptions {
      isMultiplayer
      supportsInstantJoin
      hasModeratedChat
      minLobbySize
      maxLobbySize
    }
    gameBuilds {
      ...GameBuildFragment
    }
    rawRevenueShareOptions
    revenueShareVersion
    isInvited
    videos {
      videoThumbnail
      videoOriginal
      portraitVideoThumbnail
      portraitVideoOriginal
    }
    isFullLaunch
    basicLaunchOn
    basicLaunchExpiresOn
    legalFormStatus
    statusChanges {
      status
      changedOn
      isFullLaunch
    }
    game {
      id
      addedOn
      rawLeaderboardOptions
    }
    acceptedTermsVersion
    gameJamId
    skipFileChecks
  }
  ${GAME_BUILD_FRAGMENT}
`;

export const GET_MY_SUBMISSION_QUERY = gql`
  query GetMySubmission($id: String!) {
    me {
      id
      submission(id: $id) {
        ...SubmissionFragment
      }
    }
  }
  ${GAME_BUILD_FRAGMENT}
  ${SUBMISSION_FRAGMENT}
`;

export interface MyGameStatsQuery {
  gameBySlug: GameTotalData;
}

export interface GameTotalData {
  slug: string;
  id: string;
  name: TranslatedEntry[];
  status: GAME_STATUS;
  crazygamesUrl: string;
  devStats: DeveloperGameStatsForTimespan;
  coverUrl: string;
}

export const MY_GAME_STATS_QUERY = gql`
  query gameStatsForGame($slug: String!, $period: StatsForPeriod!, $options: StatsForPeriodOptionsInput, $device: StatsDevice) {
    gameBySlug(slug: $slug) {
      id
      slug
      name {
        ...TranslatedEntryFragment
      }
      status
      coverUrl
      crazygamesUrl
      devStats(period: $period, options: $options, device: $device) {
        from
        until
        revenueShare {
          total
        }
        analytics {
          pageviews
          users
          avgTimeOnPage
          impressions
          ctr
          rating
          totalVotes
          retentionD1
          retentionD7
          returningPlayers
          avgConversion
          avgLoadingTime
          issues
        }
        benchmarks {
          ...BenchmarkStatsFragment
        }
        advertisementStats {
          ...GameAdvertisementStatsFragment
        }
      }
    }
  }
  ${TRANSLATED_ENTRY_FRAGMENT}
  ${BENCHMARK_STATS_FRAGMENT}
  ${GAME_ADVERTISEMENT_STATS_FRAGMENT}
`;

export interface BasicLaunchGameStatsQuery {
  gameBySlug: BasicLaunchGameStats;
}

export interface BasicLaunchGameStats {
  basicLaunchStatsAndBenchmarks: BasicLaunchStatsAndBenchmarks;
}

export const BASIC_LAUNCH_GAME_STATS_QUERY = gql`
  query gameStatsForGame($slug: String!) {
    gameBySlug(slug: $slug) {
      basicLaunchStatsAndBenchmarks {
        basicLaunchStart
        basicLaunchExpire
        evaluationStart
        evaluationEnd
        desktop {
          metrics {
            averagePlaytime
            retentionD1
            gameplayConversion
          }
          benchmarks {
            averagePlaytime
            retentionD1
            gameplayConversion
          }
        }
        mobile {
          metrics {
            averagePlaytime
            retentionD1
            gameplayConversion
          }
          benchmarks {
            averagePlaytime
            retentionD1
            gameplayConversion
          }
        }
      }
    }
  }
`;

export interface GameDailyData {
  id: string;
  dailyRevenueStats: DeveloperDailyRevenueStat[];
  dailyStats: DailyDeveloperGameAnalytics[] | null;
}

export interface MyGameDailyStatsQuery {
  gameBySlug: GameDailyData;
}

export const MY_GAME_DAILY_STATS_QUERY = gql`
  query gameStatsForGame($slug: String!, $options: StatsForPeriodOptionsInput!, $device: StatsDevice) {
    gameBySlug(slug: $slug) {
      id
      dailyRevenueStats(options: $options) {
        date
        revenueShareDaily {
          total
          iap
          admob
        }
      }
      dailyStats(options: $options, device: $device) {
        date
        pageviews
        users
        avgTimeOnPage
        ctr
        likes
        dislikes
        rating
        totalVotes
        impressions
        retentionD1
        retentionD7
        returningPlayers
        avgConversion
        avgLoadingTime
        issues
      }
    }
  }
`;
